import { useEffect } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import { useForm, Controller } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";

const LANGUAGES = [
	{ key: "en-us", label: "English (US)" },
	{ key: "de-ch", label: "Deutsch (CH)" },
	{ key: "de-de", label: "Deutsch (DE)" },
	{ key: "fr-fr", label: "Français" },
	{ key: "it-it", label: "Italiano" },
] as const;

const schema = z.object({
	"en-us": z.string().min(1, "English is required"),
	"de-ch": z.string().default(""),
	"de-de": z.string().default(""),
	"fr-fr": z.string().default(""),
	"it-it": z.string().default(""),
	sequence: z.coerce.number().min(0, "Sequence must be 0 or more"),
	isTextbox: z.boolean(),
});

export type DimensionOptionFormData = z.infer<typeof schema>;

interface Props {
	show: boolean;
	onHide: () => void;
	onSubmit: (data: DimensionOptionFormData) => void;
	title: string;
	defaultValues?: Partial<DimensionOptionFormData>;
}

export default function DimensionOptionFormModal({ show, onHide, onSubmit, title, defaultValues }: Props) {
	const {
		register,
		handleSubmit,
		reset,
		control,
		formState: { errors },
	} = useForm<DimensionOptionFormData>({
		resolver: zodResolver(schema) as any,
		defaultValues: {
			"en-us": "",
			"de-ch": "",
			"de-de": "",
			"fr-fr": "",
			"it-it": "",
			sequence: 10,
			isTextbox: false,
			...defaultValues,
		},
	});

	useEffect(() => {
		if (show) {
			reset({
				"en-us": "",
				"de-ch": "",
				"de-de": "",
				"fr-fr": "",
				"it-it": "",
				sequence: 10,
				isTextbox: false,
				...defaultValues,
			});
		}
	}, [show, defaultValues, reset]);

	const handleFormSubmit = (data: DimensionOptionFormData) => {
		onSubmit(data);
		onHide();
	};

	return (
		<Modal show={show} onHide={onHide} centered>
			<Modal.Header closeButton>
				<Modal.Title className="text-truncate" style={{ maxWidth: "90%" }}>
					{title}
				</Modal.Title>
			</Modal.Header>
			<form onSubmit={handleSubmit(handleFormSubmit)}>
				<Modal.Body>
					<h6 className="mb-3">Option translations</h6>

					{LANGUAGES.map((lang) => (
						<Form.Group className="mb-3" key={lang.key}>
							<Form.Label>
								{lang.label}
								{lang.key === "en-us" && <span className="text-danger ms-1">*</span>}
							</Form.Label>
							<Form.Control
								type="text"
								placeholder={lang.label}
								isInvalid={!!errors[lang.key]}
								{...register(lang.key)}
							/>
							{errors[lang.key] && (
								<Form.Control.Feedback type="invalid">
									{errors[lang.key]?.message}
								</Form.Control.Feedback>
							)}
						</Form.Group>
					))}

					<hr />
					<h6 className="mb-3">Settings</h6>

					<div className="row mb-3">
						<div className="col-md-6">
							<Form.Group>
								<Form.Label>Sequence</Form.Label>
								<Form.Control
									type="number"
									isInvalid={!!errors.sequence}
									{...register("sequence")}
								/>
								{errors.sequence && (
									<Form.Control.Feedback type="invalid">
										{errors.sequence.message}
									</Form.Control.Feedback>
								)}
							</Form.Group>
						</div>
					</div>

					<Controller
						name="isTextbox"
						control={control}
						render={({ field }) => (
							<Form.Check
								type="checkbox"
								label="Is Textbox"
								checked={field.value}
								onChange={(e) => field.onChange(e.target.checked)}
							/>
						)}
					/>
				</Modal.Body>
				<Modal.Footer>
					<Button variant="outline-secondary" onClick={onHide}>
						Cancel
					</Button>
					<Button variant="primary" type="submit">
						Save
					</Button>
				</Modal.Footer>
			</form>
		</Modal>
	);
}
